import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SectionCard, StatusPill } from "components/admin";
import { CrudSection } from "components/admin/workspace-crud";
import {
  buildSportAgeGroupCreateValues,
  buildSportAgeGroupFields,
  buildSportAgeGroupUpdateValues,
} from "pages/app/workspace/sections/forms/school-admin-section-forms";
import { workspaceApi } from "pages/app/workspace/services";
import { hasPortalAction } from "utils/portal-access";
import { ToolbarSelect } from "pages/app/workspace/sections/styles/workspace-section.styles";

export const SportAgeGroupsSection: React.FC<{ portalAccess: any }> = ({ portalAccess }) => {
  const [sportId, setSportId] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const sportsQuery = useQuery({
    queryKey: ["workspace-age-group-sports"],
    queryFn: () => workspaceApi.sports.list(),
  });
  const ageGroupsQuery = useQuery({
    queryKey: ["workspace-age-groups", sportId],
    queryFn: () => workspaceApi.sportAgeGroups.list({ sport_id: sportId || undefined }),
  });
  const canManageAgeGroups = hasPortalAction(portalAccess, "sport_age_groups.manage");
  const ageGroupFields = buildSportAgeGroupFields(sportsQuery.data || []);
  const ageGroups = useMemo(() => ageGroupsQuery.data || [], [ageGroupsQuery.data]);

  const visibleAgeGroups = useMemo(() => {
    const normalizedRows = ageGroups.filter((row: any) =>
      categoryFilter === "all" ? true : String(row.gender_category || "mixed") === categoryFilter,
    );

    return [...normalizedRows].sort((left: any, right: any) => {
      const sportComparison = String(left.sport_name || "").localeCompare(String(right.sport_name || ""));
      if (sportComparison !== 0) {
        return sportComparison;
      }

      const sortOrderComparison = Number(left.sort_order ?? Number.MAX_SAFE_INTEGER) - Number(right.sort_order ?? Number.MAX_SAFE_INTEGER);
      if (sortOrderComparison !== 0) {
        return sortOrderComparison;
      }

      const minAgeComparison = Number(left.min_age ?? Number.MAX_SAFE_INTEGER) - Number(right.min_age ?? Number.MAX_SAFE_INTEGER);
      if (minAgeComparison !== 0) {
        return minAgeComparison;
      }

      return String(left.name || "").localeCompare(String(right.name || ""));
    });
  }, [ageGroups, categoryFilter]);

  const ageGroupStats = useMemo(() => {
    const structuredRangeCount = visibleAgeGroups.filter(
      (row: any) => row.min_age !== null || row.max_age !== null,
    ).length;
    const teamMappedCount = visibleAgeGroups.filter((row: any) => String(row.team_label || "").trim()).length;

    return [
      {
        label: "Visible Groups",
        value: visibleAgeGroups.length,
        helper: "Age groups in the current filtered view.",
      },
      {
        label: "Sports Covered",
        value: new Set(visibleAgeGroups.map((row: any) => String(row.sport_id || row.sport_name || ""))).size,
        helper: "Distinct sports represented after filtering.",
      },
      {
        label: "Defined Ranges",
        value: structuredRangeCount,
        helper: "Groups that already have minimum or maximum ages set.",
      },
      {
        label: "Team Labels",
        value: teamMappedCount,
        helper: "Groups with a team-facing label such as 1st XI or Colts.",
      },
    ];
  }, [visibleAgeGroups]);

  const formatAgeRange = (row: any) => {
    const hasMinAge = row.min_age !== null && row.min_age !== undefined && row.min_age !== "";
    const hasMaxAge = row.max_age !== null && row.max_age !== undefined && row.max_age !== "";

    if (hasMinAge && hasMaxAge) {
      return `${row.min_age} to ${row.max_age} years`;
    }

    if (hasMinAge) {
      return `${row.min_age}+ years`;
    }

    if (hasMaxAge) {
      return `Up to ${row.max_age} years`;
    }

    return "Open age range";
  };

  return (
    <CrudSection
      buildCreateValues={() => buildSportAgeGroupCreateValues(sportId, sportsQuery.data || [])}
      buildUpdateValues={buildSportAgeGroupUpdateValues}
      canCreate={canManageAgeGroups}
      canEdit={canManageAgeGroups}
      canRemove={canManageAgeGroups}
      columns={[
        {
          label: "Group",
          accessor: (row) => (
            <div className="space-y-1">
              <p className="font-medium text-[var(--brand-ink)]">{row.name || "Untitled group"}</p>
              <p className="text-xs text-[var(--brand-muted)]">
                {row.team_label ? `Team label: ${row.team_label}` : "No team label added yet"}
              </p>
            </div>
          ),
        },
        {
          label: "Sport",
          accessor: (row) => (
            <div className="space-y-1">
              <p className="text-sm text-[var(--brand-ink)]">{row.sport_name || "Unknown"}</p>
              <p className="text-xs text-[var(--brand-muted)]">
                Sort order {row.sort_order !== null && row.sort_order !== undefined && row.sort_order !== "" ? row.sort_order : "not set"}
              </p>
            </div>
          ),
        },
        { label: "Range", accessor: (row) => formatAgeRange(row) },
        { label: "Category", accessor: (row) => <StatusPill value={row.gender_category} /> },
      ]}
      createLabel="Add age group"
      data={visibleAgeGroups}
      description={
        canManageAgeGroups
          ? "Keep age bands readable and structured so team setup, rosters, and selection flows stay clear."
          : "Review the age-group structure currently driving school-team and roster flows."
      }
      fields={ageGroupFields}
      helperCard={
        <SectionCard
          subtitle="Use the filters to narrow the list first, then scan by group name, age span, and team label instead of parsing every field row by row."
          title="How To Read This View"
        >
          <div className="grid gap-3 md:grid-cols-3">
            <div className="brand-panel-muted">
              <p className="text-sm font-medium text-[var(--brand-ink)]">Identity first</p>
              <p className="mt-1 text-xs leading-6 text-[var(--brand-muted)]">
                Group names and team labels are surfaced together so you can see what coaches will actually recognise.
              </p>
            </div>
            <div className="brand-panel-muted">
              <p className="text-sm font-medium text-[var(--brand-ink)]">Range clarity</p>
              <p className="mt-1 text-xs leading-6 text-[var(--brand-muted)]">
                Open, upper-only, and full age ranges are translated into plain language instead of raw number pairs.
              </p>
            </div>
            <div className="brand-panel-muted">
              <p className="text-sm font-medium text-[var(--brand-ink)]">Quicker filtering</p>
              <p className="mt-1 text-xs leading-6 text-[var(--brand-muted)]">
                Filter by sport and gender category before editing so the drawer stays focused on one branch at a time.
              </p>
            </div>
          </div>
        </SectionCard>
      }
      invalidateKeys={[["workspace-age-groups", sportId], ["workspace-age-group-sports"]]}
      isLoading={ageGroupsQuery.isLoading}
      onCreate={(payload) => workspaceApi.sportAgeGroups.create(payload)}
      onRemove={(row) => workspaceApi.sportAgeGroups.remove(row.id)}
      onUpdate={(row, payload) => workspaceApi.sportAgeGroups.update(row.id, payload)}
      stats={ageGroupStats}
      title="Sport Age Groups"
      toolbar={
        <div className="grid gap-3 md:grid-cols-2">
          <ToolbarSelect onChange={(event) => setSportId(event.target.value)} value={sportId}>
            <option value="">All sports</option>
            {(sportsQuery.data || []).map((sport: any) => (
              <option key={sport.id} value={sport.id}>
                {sport.name}
              </option>
            ))}
          </ToolbarSelect>

          <ToolbarSelect
            onChange={(event) => setCategoryFilter(event.target.value)}
            value={categoryFilter}
          >
            <option value="all">All categories</option>
            <option value="mixed">Mixed</option>
            <option value="boys">Boys</option>
            <option value="girls">Girls</option>
          </ToolbarSelect>
        </div>
      }
    />
  );
};
